import {
  Check,
  Navigation,
  Pause,
  ShieldAlert,
  StickyNote,
  Truck,
  Warehouse,
} from "lucide-react";
import { ZOOM_LEVELS } from "@/components/markers/marker.util";
import { LANE_ORDER, LANE_META, type LaneCounts } from "../task-type-meta";

export type ZoomTier = "far" | "mid" | "near";

export function zoomTier(zoomLevel: number): ZoomTier {
  if (zoomLevel < ZOOM_LEVELS.MEDIUM) return "far";
  if (zoomLevel < ZOOM_LEVELS.LARGE) return "mid";
  return "near";
}

const INK = "#0b0e14"; // marker outline / stem

// ── shared primitives ───────────────────────────────────────────────────────

/** Corner care badges — note (white) + fragile/add-on (teal). Real icons. */
function CareBadges({
  note,
  fragile,
  scale = 1,
}: {
  note: boolean;
  fragile: boolean;
  scale?: number;
}) {
  if (!note && !fragile) return null;
  const s = 17 * scale;
  const icon = 11 * scale;
  const box: React.CSSProperties = {
    width: s,
    height: s,
    borderRadius: 5 * scale,
    border: `${1.5 * scale}px solid ${INK}`,
    display: "grid",
    placeItems: "center",
    boxShadow: "0 1px 4px rgba(0,0,0,.45)",
  };
  return (
    <div
      className="absolute -right-1 -top-2 flex"
      style={{ gap: 2 * scale, zIndex: 6 }}
    >
      {note && (
        <div
          style={{ ...box, background: "#fff", color: "#18181b" }}
          title="Has notes"
        >
          <StickyNote style={{ width: icon, height: icon }} strokeWidth={2.4} />
        </div>
      )}
      {fragile && (
        <div
          style={{ ...box, background: "#0d9488", color: "#fff" }}
          title="Fragile / add-on"
        >
          <ShieldAlert
            style={{ width: icon, height: icon }}
            strokeWidth={2.4}
          />
        </div>
      )}
    </div>
  );
}

function Stem({
  color = INK,
  extended = false,
}: {
  color?: string;
  extended?: boolean;
}) {
  return (
    <div className="flex flex-col items-center" style={{ marginTop: 1 }}>
      <div style={{ width: 2, height: 7, background: color }} />
      {!extended && (
        <div
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "#fff",
            border: `1.5px solid ${color}`,
          }}
        />
      )}
    </div>
  );
}

function UrgencyRim({
  urgency,
  radius,
  children,
}: {
  urgency: "red" | "orange" | null;
  radius: number;
  children: React.ReactNode;
}) {
  if (!urgency) return <>{children}</>;
  const c = urgency === "red" ? "#dc2626" : "#f59e0b";
  return (
    <div
      className={`relative ${urgency === "red" ? "animate-pulse" : ""}`}
      style={{ borderRadius: radius + 4 }}
    >
      <div
        className="pointer-events-none absolute inset-[-3px]"
        style={{
          borderRadius: radius + 5,
          boxShadow: `0 0 0 2px ${c}, 0 0 10px ${urgency === "red" ? "rgba(220,38,38,.5)" : "rgba(245,158,11,.45)"}`,
        }}
      />
      {children}
    </div>
  );
}

// ── task cluster (unassigned work) ──────────────────────────────────────────
export function TaskClusterMarker({
  counts,
  urgency,
  note,
  fragile,
  tier,
  held = false,
  onClick,
}: {
  counts: LaneCounts;
  urgency: "red" | "orange" | null;
  note: boolean;
  fragile: boolean;
  tier: ZoomTier;
  held?: boolean;
  onClick: () => void;
}) {
  const lanes = LANE_ORDER.filter((k) => counts[k] > 0);
  if (lanes.length === 0) return null;

  if (tier === "far") {
    return (
      <button type="button" onClick={onClick} className="block">
        <div
          style={{
            width: 13,
            height: 13,
            borderRadius: "50%",
            background: held ? "#71717a" : LANE_META[lanes[0]].color,
            border: `1.5px solid ${INK}`,
            boxShadow: "0 1px 4px rgba(0,0,0,.4)",
          }}
        />
      </button>
    );
  }

  const near = tier === "near";
  const h = near ? 30 : 24;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center focus:outline-hidden"
      title={held ? "Cancellation review — assignment on hold" : undefined}
    >
      <UrgencyRim urgency={urgency} radius={near ? 9 : 7}>
        <div className="relative">
          <div
            className="flex items-stretch overflow-hidden"
            style={{
              height: h,
              borderRadius: 8,
              border: `2px solid ${INK}`,
              boxShadow: "0 4px 12px -3px rgba(0,0,0,.45)",
            }}
          >
            {held ? (
              <div
                className="grid place-items-center bg-zinc-500 px-3 text-white"
                style={{ minWidth: near ? 34 : 28 }}
              >
                <Pause className="h-4 w-4" fill="currentColor" />
              </div>
            ) : (
              lanes.map((k) => (
                <div
                  key={k}
                  className="grid place-items-center"
                  style={{
                    background: LANE_META[k].color,
                    minWidth: near ? 26 : 21,
                    padding: "0 5px",
                  }}
                >
                  <span
                    className="font-bold tabular-nums text-white"
                    style={{ fontSize: near ? 13 : 11 }}
                  >
                    {counts[k]}
                  </span>
                </div>
              ))
            )}
          </div>
          <CareBadges note={note} fragile={fragile} scale={near ? 1 : 0.85} />
        </div>
      </UrgencyRim>
      <Stem />
    </button>
  );
}

// ── waypoint (a stop inside a trip) ─────────────────────────────────────────
export type StopMarkerStatus = "pending" | "active" | "done";

export function WaypointMarker({
  seq,
  color,
  status,
  counts,
  note,
  fragile,
  tier,
  holdCount = 0,
  allHeld = false,
  extendedStem = false,
  onClick,
}: {
  seq: number;
  color: string;
  status: StopMarkerStatus;
  counts: LaneCounts;
  note: boolean;
  fragile: boolean;
  tier: ZoomTier;
  holdCount?: number;
  allHeld?: boolean;
  extendedStem?: boolean;
  onClick?: () => void;
}) {
  const done = status === "done";
  const active = status === "active";
  const total =
    counts.pickup + counts.delivery + counts.dropoff + counts.return;

  if (tier === "far") {
    return (
      <button type="button" onClick={onClick} className="block">
        <div
          className={active ? "animate-pulse" : ""}
          style={{
            width: 11,
            height: 11,
            borderRadius: "50%",
            background: done || allHeld ? "#94a3b8" : color,
            border: "1.5px solid #fff",
            boxShadow: "0 1px 3px rgba(0,0,0,.4)",
            opacity: done ? 0.7 : 1,
          }}
        />
      </button>
    );
  }

  const near = tier === "near";
  const sz = near ? 30 : 24;
  const lanes = LANE_ORDER.filter((k) => counts[k] > 0);

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center focus:outline-hidden"
      style={{ opacity: done ? 0.78 : 1 }}
      title={
        holdCount > 0
          ? "Cancellation review — held work at this stop"
          : undefined
      }
    >
      <div className="relative">
        {active && (
          <div
            className="pointer-events-none absolute inset-[-3px] animate-pulse"
            style={{
              borderRadius: 10,
              boxShadow: `0 0 0 2px ${color}, 0 0 10px ${color}88`,
            }}
          />
        )}
        <div
          className="relative grid place-items-center font-bold tabular-nums text-white"
          style={{
            width: sz,
            height: sz,
            borderRadius: 9,
            background: done || allHeld ? "#94a3b8" : color,
            border: `2px solid ${INK}`,
            boxShadow: "0 4px 12px -3px rgba(0,0,0,.45)",
            fontSize: near ? 13 : 11,
          }}
        >
          {done ? (
            <Check style={{ width: 15, height: 15 }} strokeWidth={3} />
          ) : allHeld ? (
            <Pause style={{ width: 14, height: 14 }} fill="currentColor" />
          ) : active ? (
            <Navigation style={{ width: 13, height: 13 }} />
          ) : (
            seq
          )}
        </div>
        {!done && total > 1 && near && (
          <div
            className="absolute -bottom-1.5 left-1/2 flex -translate-x-1/2 overflow-hidden"
            style={{
              border: `1.5px solid ${INK}`,
              borderRadius: 999,
              boxShadow: "0 1px 3px rgba(0,0,0,.4)",
            }}
          >
            {lanes.map((k) => (
              <div
                key={k}
                style={{ width: 7, height: 7, background: LANE_META[k].color }}
              />
            ))}
          </div>
        )}
        <CareBadges note={note} fragile={fragile} scale={near ? 0.95 : 0.82} />
        {!done && holdCount > 0 && !allHeld && (
          <span className="absolute -left-2 -top-2 grid h-4 min-w-4 place-items-center rounded-full bg-zinc-600 px-1 text-[9px] font-bold text-white ring-2 ring-white">
            <Pause className="h-2.5 w-2.5" fill="currentColor" />
          </span>
        )}
      </div>
      <Stem color={done || allHeld ? "#94a3b8" : INK} extended={extendedStem} />
    </button>
  );
}

// ── operator (live driver) ──────────────────────────────────────────────────
export function OperatorMarker({
  initials,
  color,
  vehicle,
  rolling,
  tier,
  onClick,
}: {
  initials: string;
  color: string;
  vehicle: boolean;
  rolling: boolean;
  tier: ZoomTier;
  onClick?: () => void;
}) {
  const sz = tier === "far" ? 22 : 34;
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex items-center justify-center focus:outline-hidden"
    >
      <div
        className="grid place-items-center font-bold text-white"
        style={{
          width: sz,
          height: sz,
          borderRadius: "50%",
          background: color,
          border: "2.5px solid #fff",
          boxShadow: "0 3px 10px rgba(0,0,0,.4)",
          fontSize: tier === "far" ? 9 : 12,
        }}
      >
        {initials}
      </div>
      {tier !== "far" && vehicle && (
        <div
          className="absolute -bottom-1 -right-1 grid place-items-center rounded-full bg-white"
          style={{
            width: 16,
            height: 16,
            border: `1.5px solid ${color}`,
            color,
          }}
        >
          <Truck style={{ width: 9, height: 9 }} strokeWidth={2.4} />
        </div>
      )}
      {rolling && (
        <span
          className="absolute -left-1 -top-1 animate-pulse"
          style={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: "#16a34a",
            border: "1.5px solid #fff",
          }}
        />
      )}
    </button>
  );
}

// ── operator stack (+N overflow) ─────────────────────────────────────────────
export function StackedOperators({
  items,
  max = 3,
  onClick,
}: {
  items: { initials: string; color: string }[];
  max?: number;
  onClick?: () => void;
}) {
  const shown = items.slice(0, max);
  const extra = items.length - shown.length;
  const sz = 30;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center focus:outline-hidden"
    >
      {shown.map((it, i) => (
        <div
          key={i}
          className="grid place-items-center rounded-full font-bold text-white"
          style={{
            width: sz,
            height: sz,
            marginLeft: i ? -sz * 0.42 : 0,
            zIndex: shown.length - i,
            background: it.color,
            border: "2px solid #fff",
            boxShadow: "0 3px 9px rgba(0,0,0,.35)",
            fontSize: 11,
          }}
        >
          {it.initials}
        </div>
      ))}
      {extra > 0 && (
        <div
          className="grid place-items-center rounded-full bg-white font-bold text-zinc-700"
          style={{
            width: sz,
            height: sz,
            marginLeft: -sz * 0.42,
            border: "2px solid #fff",
            boxShadow: "0 2px 6px rgba(0,0,0,.3)",
            fontSize: 11,
          }}
        >
          +{extra}
        </div>
      )}
    </button>
  );
}

// ── depot ───────────────────────────────────────────────────────────────────
export function DepotMarker({ tier, name }: { tier: ZoomTier; name?: string }) {
  const sz = tier === "far" ? 20 : 28;
  return (
    <div className="flex flex-col items-center" title={name}>
      <div
        className="grid place-items-center text-white"
        style={{
          width: sz,
          height: sz,
          borderRadius: 7,
          background: "#27272a",
          border: "2px solid #fff",
          boxShadow: "0 3px 9px rgba(0,0,0,.4)",
        }}
      >
        <Warehouse
          style={{
            width: tier === "far" ? 12 : 16,
            height: tier === "far" ? 12 : 16,
          }}
        />
      </div>
    </div>
  );
}
